import { forwardRef, useMemo } from "react";
import { Flex, Box } from "@chakra-ui/react";
import { Text12 } from "components/Text";
import { chakraComponents } from "chakra-react-select";
import { TASK_STATUSES } from "features/Resources/Task/constants";
import { TaskStatus } from "features/Resources/Task/models";
import { useUpdateTask } from "features/Resources/Task/hooks";
import { useToast } from "@chakra-ui/react";
import Select from "components/Select";

export const InlineStatusOption = forwardRef<any, any>(({ data, innerProps }, ref) => (
  <Flex
    ref={ref}
    {...innerProps}
    align="center"
    gap={2}
    px={3}
    py={2}
    cursor="pointer"
    _hover={{ bg: "gray.100" }}
    minWidth="150px"
  >
    <Box w={2} h={2} borderRadius="full" bg={data.colors} />
    <Text12>{data.label}</Text12>
  </Flex>
));
InlineStatusOption.displayName = "InlineStatusOption";

export const InlineStatusSingleValue = forwardRef<any, any>(({ children, ...props }, ref) => (
  <chakraComponents.SingleValue {...props} innerRef={ref}>
    <Flex align="center" gap={2}>
      <Box w={2} h={2} borderRadius="full" bg={props.data.colors} />
      <Text12>{props.data.label}</Text12>
    </Flex>
  </chakraComponents.SingleValue>
));
InlineStatusSingleValue.displayName = "InlineStatusSingleValue";

interface InlineTaskStatusSelectorProps {
  taskId: string;
  currentStatus: TaskStatus;
}

export default function InlineTaskStatusSelector({ taskId, currentStatus }: InlineTaskStatusSelectorProps) {
  const toast = useToast();
  const { mutate: updateTask } = useUpdateTask(taskId);

  // Determine if the status can be changed
  // Can only change if status is active, in_progress, or blocked
  const isEditable = useMemo(() => {
    return ["active", "in_progress", "blocked"].includes(currentStatus);
  }, [currentStatus]);

  // Only include "failed", "upcoming", and the deprecated "removed" in options if they're the current value
  const statusOptions = useMemo(() => {
    return Array.from(TASK_STATUSES.values()).filter(
      (status) =>
        (status.value !== "failed" || currentStatus === "failed") &&
        (status.value !== "upcoming" || currentStatus === "upcoming") &&
        (status.value !== "removed" || currentStatus === "removed"),
    );
  }, [currentStatus]);

  const selectedOption = TASK_STATUSES.get(currentStatus) || null;

  const handleChange = (option: any) => {
    if (!isEditable || !option || option.value === currentStatus) return;

    updateTask(
      { status: option.value },
      {
        onSuccess: () => {
          toast({
            title: "Task status updated",
            status: "success",
            duration: 2000,
          });
        },
        onError: () => {
          toast({
            title: "Failed to update task status",
            status: "error",
            duration: 3000,
          });
        },
      },
    );
  };

  return (
    <Select
      name="inlineTaskStatus"
      value={selectedOption}
      onChange={handleChange}
      options={statusOptions}
      components={{
        Option: InlineStatusOption,
        SingleValue: InlineStatusSingleValue,
        DropdownIndicator: null,
      }}
      isSearchable={false}
      variant="ghost"
      size="sm"
      isClearable={false}
      menuIsOpen={isEditable ? undefined : false}
      chakraStyles={{
        container: (base) => ({
          ...base,
          minWidth: "120px",
          cursor: isEditable ? "pointer" : "default",
        }),
        control: (base) => ({
          ...base,
          minHeight: "unset",
          cursor: isEditable ? "pointer" : "default",
          border: "none",
          bg: "transparent",
          pointerEvents: isEditable ? "auto" : "none",
          ...(isEditable && {
            _hover: {
              bg: "gray.50",
            },
          }),
        }),
        valueContainer: (base) => ({
          ...base,
          padding: "2px 4px",
          cursor: isEditable ? "pointer" : "default",
        }),
      }}
    />
  );
}
